import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanAdminMailThread,
  fetchYowanAdminMailSenderIdentities,
  getYowanApiFieldErrorMessage,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const splitAddressInput = (value = '') =>
  String(value || '')
    .split(/[\n,;]/)
    .map((item) => item.trim())
    .filter(Boolean);

const getMailApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};
  const errors = responseData.errors || responseData;
  const fieldMessage = getYowanApiFieldErrorMessage({ response: { data: errors } }, [
    'sender_identity_id',
    'to',
    'cc',
    'bcc',
    'subject',
    'body',
    'non_field_errors',
    'detail',
  ]);

  return fieldMessage ? `${fallbackMessage} ${fieldMessage}` : fallbackMessage;
};

const AdminMailNewPage = () => {
  const content = yowanPageContent['/admin/mail/new'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const [senderIdentities, setSenderIdentities] = useState([]);
  const [isLoadingIdentities, setIsLoadingIdentities] = useState(true);
  const [identityError, setIdentityError] = useState('');
  const [formValues, setFormValues] = useState({
    senderIdentityId: '',
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
  });
  const [submitMessage, setSubmitMessage] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingIdentities(true);
    setIdentityError('');

    fetchYowanAdminMailSenderIdentities()
      .then((items) => {
        if (!isMounted) {
          return;
        }

        setSenderIdentities(items);
        const defaultIdentity = items.find((item) => item.isDefault) || items[0] || null;
        if (defaultIdentity) {
          setFormValues((current) => ({
            ...current,
            senderIdentityId: current.senderIdentityId || defaultIdentity.id,
          }));
        }
      })
      .catch(() => {
        if (isMounted) {
          setSenderIdentities([]);
          setIdentityError(content?.senderIdentityLoadErrorMessage || '送信元を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingIdentities(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.senderIdentityLoadErrorMessage]);

  const selectedIdentity = useMemo(
    () => senderIdentities.find((item) => item.id === formValues.senderIdentityId) || null,
    [formValues.senderIdentityId, senderIdentities]
  );

  if (!content) {
    return null;
  }

  const updateValue = (name) => (event) => {
    setFormValues((current) => ({
      ...current,
      [name]: event.target.value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage('');
    setBulkResult(null);

    if (!formValues.senderIdentityId) {
      setSubmitMessage('送信元を選択してください。');
      return;
    }

    const to = splitAddressInput(formValues.to);
    if (!to.length) {
      setSubmitMessage('宛先を入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      const result = await createYowanAdminMailThread({
        senderIdentityId: formValues.senderIdentityId,
        to,
        cc: splitAddressInput(formValues.cc),
        bcc: splitAddressInput(formValues.bcc),
        subject: formValues.subject.trim(),
        body: formValues.body,
      });

      if (result?.mode === 'bulk') {
        const createdCount = result.createdCount || result.threads?.length || result.results?.length || to.length;
        setBulkResult(result);
        setSubmitMessage(
          (content.form.bulkSuccessMessage || '{count}件のメール送信を受け付けました。メール一覧で作成されたスレッドを確認してください。')
            .replace('{count}', String(createdCount))
        );
        setFormValues((current) => ({
          ...current,
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          body: '',
        }));
        return;
      }

      if (result?.thread?.id) {
        navigate(appendYowanPreviewQueryIfNeeded(`/admin/mail/threads/${result.thread.id}`, preservedDomainSearch));
        return;
      }

      setSubmitMessage(content.form.successMessage || 'メール送信を受け付けました。メール一覧で確認してください。');
    } catch (error) {
      setSubmitMessage(getMailApiErrorMessage(error, content.form.errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-mail" to={appendYowanPreviewQueryIfNeeded('/admin/mail', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={onSubmit}>
        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.senderIdentity}</span>
          <select
            className="c-form__control"
            value={formValues.senderIdentityId}
            disabled={isLoadingIdentities || isSubmitting}
            onChange={updateValue('senderIdentityId')}
            required
          >
            <option value="">送信元を選択</option>
            {senderIdentities.map((identity) => (
              <option key={identity.id} value={identity.id}>
                {[identity.label, identity.email].filter(Boolean).join(' / ')}
              </option>
            ))}
          </select>
          <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
            {selectedIdentity
              ? `From: ${selectedIdentity.email}${selectedIdentity.replyToEmail ? ` / Reply-To: ${selectedIdentity.replyToEmail}` : ''}`
              : content.form.senderIdentityHelpText}
          </p>
          {identityError ? <em className="yowan-admin-form__error">{identityError}</em> : null}
        </label>

        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.to}</span>
          <textarea
            className="c-form__control c-form__control--textarea"
            rows={3}
            value={formValues.to}
            onChange={updateValue('to')}
            disabled={isSubmitting}
            required
          />
          <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
            {content.form.addressHelpText}
          </p>
        </label>

        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.cc}</span>
          <input className="c-form__control" value={formValues.cc} onChange={updateValue('cc')} disabled={isSubmitting} />
        </label>

        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.bcc}</span>
          <input className="c-form__control" value={formValues.bcc} onChange={updateValue('bcc')} disabled={isSubmitting} />
        </label>

        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.subject}</span>
          <input
            className="c-form__control"
            value={formValues.subject}
            onChange={updateValue('subject')}
            disabled={isSubmitting}
            required
          />
        </label>

        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.body}</span>
          <textarea
            className="c-form__control c-form__control--textarea"
            rows={12}
            value={formValues.body}
            onChange={updateValue('body')}
            disabled={isSubmitting}
            required
          />
        </label>

        <div className="yowan-admin-form__actions yowan-admin-form__actions--stacked">
          <button
            type="submit"
            className="c-form__submit"
            disabled={isSubmitting || isLoadingIdentities || !senderIdentities.length}
          >
            {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
          </button>
          {isSubmitting || submitMessage ? (
            <div className="yowan-admin-form__action-feedback">
              {isSubmitting ? <LoadingSpinner /> : null}
              {submitMessage ? <p className="yowan-admin-form__field-note">{submitMessage}</p> : null}
              {bulkResult ? (
                <Link to={appendYowanPreviewQueryIfNeeded('/admin/mail', preservedDomainSearch)}>
                  {content.form.bulkListLinkLabel || 'メール一覧へ'}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </form>
    </AdminBlogShell>
  );
};

export default AdminMailNewPage;
